import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { currentSelectedLanguage } from "../../../../StateManagement/actions";
import contactsService from "../../../../Services/contactsService";
import "./ContactManagement.css";

// Portal Dropdown Component to render popovers directly at document.body
function PortalGroupDropdown({ triggerRef, isOpen, onClose, title, actionText, onAction, searchValue, onSearchChange, searchPlaceholder, items = [], onToggleItem, }) {
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const popoverRef = useRef(null);

  const updatePosition = () => {
    if (!triggerRef || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const popoverWidth = 240;
    const estimatedPopoverHeight = 230;
    let left = rect.right - popoverWidth;
    if (left < 10) left = Math.max(10, rect.left);
    if (left + popoverWidth > window.innerWidth - 10) {
      left = window.innerWidth - popoverWidth - 10;
    }
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    let top;
    if (spaceBelow < estimatedPopoverHeight && spaceAbove > spaceBelow) {
      top = Math.max(10, rect.top - estimatedPopoverHeight - 4);
    } else {
      top = rect.bottom + 4;
    }
    setCoords({ top, left });
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      const handleScrollOrResize = () => { updatePosition(); };
      window.addEventListener("resize", handleScrollOrResize);
      window.addEventListener("scroll", handleScrollOrResize, true);
      return () => {
        window.removeEventListener("resize", handleScrollOrResize);
        window.removeEventListener("scroll", handleScrollOrResize, true);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target) && triggerRef.current && !triggerRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen, onClose, triggerRef]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div ref={popoverRef} className="save-directory-dropdown-popover save-directory-portal-popover" onClick={(e) => e.stopPropagation()}
      style={{ position: "fixed", top: `${coords.top}px`, left: `${coords.left}px`, width: "240px", zIndex: 99999, }}>
      <div className="save-directory-dropdown-popover-header">
        <span className="save-directory-dropdown-popover-title">{title}</span>
        {actionText && (
          <button type="button" onClick={(e) => { e.stopPropagation(); onAction(); }} className="save-directory-dropdown-popover-action">
            {actionText}
          </button>
        )}
      </div>
      <div className="save-directory-dropdown-search-box">
        <span className="material-icons save-directory-dropdown-search-icon">search</span>
        <input type="text" placeholder={searchPlaceholder} value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)} className="save-directory-dropdown-search-input" autoFocus />
      </div>
      <div className="save-directory-dropdown-list">
        {items.length === 0 ? (<div className="save-directory-empty py-2 font-11">No matching groups</div>) : (
          items.map((item) => (
            <div
              key={item.id}
              onClick={(e) => {
                e.stopPropagation();
                if (!item.isDisabled) onToggleItem(item.id);
              }}
              className={`save-directory-dropdown-item ${item.isChecked ? "checked" : ""} ${item.isDisabled ? "disabled opacity-50 cursor-not-allowed" : ""}`}
              style={item.isDisabled ? { opacity: 0.65, cursor: "not-allowed", backgroundColor: "#f8f9fa" } : {}}
            >
              <div className="save-directory-dropdown-item-left">
                <div className={`save-directory-custom-checkbox ${item.isChecked ? "checked" : ""}`}>
                  {item.isChecked && <span className="material-icons">check</span>}
                </div>
                <span className="save-directory-dropdown-item-name">{item.name}</span>
              </div>
              {item.badgeText && (
                <span className="font-10 text-muted ms-auto me-1 fw-normal">{item.badgeText}</span>
              )}
            </div>
          ))
        )}
      </div>
    </div>,
    document.body
  );
}

export function SaveToDirectoryModal({ isOpen, onClose, unsavedContacts = [], pendingContacts = [], contactGroups = [], transactionRecipients = [], onProceed, }) {
  const selectedLanguage = useSelector(currentSelectedLanguage);

  const effectiveUnsaved = useMemo(() => {
    if (unsavedContacts && Array.isArray(unsavedContacts) && unsavedContacts.length > 0) {
      return unsavedContacts;
    }
    if (pendingContacts && Array.isArray(pendingContacts) && pendingContacts.length > 0) {
      return pendingContacts;
    }
    return [];
  }, [unsavedContacts, pendingContacts]);

  const [localContactGroups, setLocalContactGroups] = useState(contactGroups || []);

  useEffect(() => {
    if (contactGroups && contactGroups.length > 0) {
      setLocalContactGroups(contactGroups);
    }
  }, [contactGroups]);

  useEffect(() => {
    if (isOpen) {
      contactsService.getContactGroups("Personal")
        .then((groups) => {
          if (Array.isArray(groups)) {
            setLocalContactGroups(groups);
          }
        })
        .catch((err) => console.error("Error fetching personal groups for modal:", err));
    }
  }, [isOpen]);

  // Helper functions for parsing contact representation
  const getContactEmail = (c) => {
    if (!c) return "";
    if (typeof c === "string") return c.toLowerCase().trim();
    if (Array.isArray(c)) return (c[0] || "").toLowerCase().trim();
    return (c.email || c.Email || "").toLowerCase().trim();
  };

  const getContactFirstName = (c) => {
    if (!c) return "";
    if (typeof c === "object" && !Array.isArray(c)) return c.firstName || c.FirstName || "";
    if (Array.isArray(c)) return c[1] || "";
    return "";
  };

  const getContactLastName = (c) => {
    if (!c) return "";
    if (typeof c === "object" && !Array.isArray(c)) return c.lastName || c.LastName || "";
    if (Array.isArray(c)) return c[2] || "";
    return "";
  };

  // Sort contact groups in ascending order by group name
  const sortedContactGroups = useMemo(() => {
    const grps = Array.isArray(localContactGroups) ? localContactGroups : (contactGroups || []);
    if (!grps || !Array.isArray(grps)) return [];
    return [...grps].sort((a, b) => {
      const nameA = (a?.name || a?.Name || "").toLowerCase();
      const nameB = (b?.name || b?.Name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [localContactGroups, contactGroups]);

  // Helper: Returns true if the contact has at least one group available to join (i.e. not already a member)
  const canContactJoinAnyGroup = useCallback((contact) => {
    if (!sortedContactGroups || sortedContactGroups.length === 0) return false;
    const contactExistingGroupIds = (contact?.existingGroupIds || []).map((id) => String(id).toLowerCase().trim());
    return sortedContactGroups.some((group) => {
      const groupId = String(group.id || group._id || group.Id || "").toLowerCase().trim();
      return groupId && !contactExistingGroupIds.includes(groupId);
    });
  }, [sortedContactGroups]);

  // Filter contacts to only those eligible for saving:
  // - New contacts (!isExistingContact) can be saved to Contacts directory (and groups if available)
  // - Existing contacts (isExistingContact) can ONLY be saved if there is at least one group available to join
  const eligibleContacts = useMemo(() => {
    if (!effectiveUnsaved || !Array.isArray(effectiveUnsaved)) return [];
    return effectiveUnsaved.filter((contact) => {
      const isExistingContact = Boolean(contact?.isExistingContact);
      if (!isExistingContact) return true;
      return canContactJoinAnyGroup(contact);
    });
  }, [effectiveUnsaved, canContactJoinAnyGroup]);

  const hasAutoProceededRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasAutoProceededRef.current = false;
      return;
    }
    // If modal is opened but there are no eligible contacts to save (e.g. all contacts already exist in contacts and user has no personal groups to join)
    if (isOpen && eligibleContacts.length === 0 && !hasAutoProceededRef.current) {
      hasAutoProceededRef.current = true;
      if (typeof onProceed === "function") {
        onProceed([], false, null);
      } else if (typeof onClose === "function") {
        onClose();
      }
    }
  }, [isOpen, eligibleContacts.length, onProceed, onClose]);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [shouldSaveGroups, setShouldSaveGroups] = useState(true);

  // Active batch selection mode: 'contacts' | 'both' | 'group' | null
  const [activeBatchMode, setActiveBatchMode] = useState("contacts");

  // Default target group(s) (supports multiple default groups!)
  const [defaultGroupIds, setDefaultGroupIds] = useState([]);
  const [isDefaultGroupsOpen, setIsDefaultGroupsOpen] = useState(false);
  const [defaultGroupsSearch, setDefaultGroupsSearch] = useState("");

  // Per-contact destination mode: 'contacts' | 'both' | 'group'
  const [destinations, setDestinations] = useState({});
  // Per-contact custom group overrides: maps contact email -> array of group IDs (string[])
  const [contactGroupOverrides, setContactGroupOverrides] = useState({});

  // Track which contact's group dropdown is open
  const [openDropdownEmail, setOpenDropdownEmail] = useState(null);
  const [groupDropdownSearch, setGroupDropdownSearch] = useState("");

  // Trigger DOM refs
  const defaultGroupTriggerRef = useRef(null);
  const rowTriggerRefs = useRef({});

  // Initialize selection and destinations
  useEffect(() => {
    if (isOpen && eligibleContacts && eligibleContacts.length > 0) {
      const emails = eligibleContacts.map(getContactEmail).filter(Boolean);
      setSelectedIds(new Set(emails));
      setSearchQuery("");
      setShouldSaveGroups(true);
      setIsDefaultGroupsOpen(false);
      setOpenDropdownEmail(null);
      setDefaultGroupsSearch("");
      setGroupDropdownSearch("");

      // 1. Check if any transaction recipient in To field is a DL matching a group
      let matchedDlGroupId = "";
      if (transactionRecipients && Array.isArray(transactionRecipients) && transactionRecipients.length > 0) {
        for (const r of transactionRecipients) {
          if (!r) continue;
          const isDlRecipient = r.isGroup === true || r.isDLGroup === true || r[10] === true || r[2] === "(Distribution List)" || (typeof r === "object" && (r.firstName === "(Distribution List)" || r.FirstName === "(Distribution List)"));
          const rEmail = String(typeof r === "string" ? r : r.email || r.Email || r[0] || "").trim().toLowerCase();
          const rName = String(typeof r === "object" ? r.name || r.Name || r[2] || "" : "").trim().toLowerCase();
          const rId = String(r.id || r._id || r.groupId || r.GroupId || "").trim().toLowerCase();
          const foundGroup = sortedContactGroups.find((g) => {
            if (!g) return false;
            const gId = String(g.id || g._id || g.Id || "").trim().toLowerCase();
            const gAlias = (g.aliasEmail || g.AliasEmail || g.email || g.Email || "").trim().toLowerCase();
            const gName = (g.name || g.Name || "").trim().toLowerCase();
            if (gId && rId && gId === rId) return true;
            if (gAlias && rEmail && gAlias === rEmail) return true;
            if (gName && rEmail && gName === rEmail) return true;
            if (gName && rName && gName === rName && rName !== "(distribution list)") return true;
            if (isDlRecipient && (gAlias === rEmail || gName === rEmail)) return true;
            return false;
          });
          if (foundGroup) {
            matchedDlGroupId = foundGroup.id || foundGroup._id || foundGroup.Id || "";
            break;
          }
        }
      }

      // 2. Default target group: matched DL group if present in To field, otherwise first group in ascending order
      let defaultGrps = [];
      if (matchedDlGroupId) {
        defaultGrps = [matchedDlGroupId];
      } else if (sortedContactGroups.length > 0) {
        const firstGrpId = sortedContactGroups[0].id || sortedContactGroups[0]._id || sortedContactGroups[0].Id || "";
        if (firstGrpId) defaultGrps = [firstGrpId];
      }
      setDefaultGroupIds(defaultGrps);
      const initialDestinations = {};
      const initialOverrides = {};
      eligibleContacts.forEach((contact) => {
        const contactEmail = getContactEmail(contact);
        if (!contactEmail) return;
        const isContactExistingInDirectory = Boolean(contact.isExistingContact);
        const canJoinGroups = canContactJoinAnyGroup(contact);
        // Scenario 1: Contact already exists in contacts directory -> force destination mode to "group" if groups are available, otherwise "contacts"
        // If not an existing contact -> default to "contacts"
        initialDestinations[contactEmail] = isContactExistingInDirectory ? (canJoinGroups ? "group" : "contacts") : "contacts";

        // Scenario 2: Exclude groups that the contact is ALREADY a member of from initial defaults
        const contactExistingGroupIds = (contact.existingGroupIds || []).map((id) => String(id).toLowerCase().trim());
        const validDefaultGroups = defaultGrps.filter((gId) => !contactExistingGroupIds.includes(String(gId).toLowerCase().trim()));
        if (validDefaultGroups.length > 0) {
          initialOverrides[contactEmail] = [...validDefaultGroups];
        } else {
          const availableGroup = sortedContactGroups.find((g) => {
            const gId = String(g.id || g._id || g.Id || "").toLowerCase().trim();
            return gId && !contactExistingGroupIds.includes(gId);
          });
          const availableGroupId = availableGroup ? (availableGroup.id || availableGroup._id || availableGroup.Id || "") : "";
          initialOverrides[contactEmail] = availableGroupId ? [availableGroupId] : [];
        }
      });
      setDestinations(initialDestinations);
      setContactGroupOverrides(initialOverrides);

      const areAllContactsExisting = eligibleContacts.every((c) => Boolean(c.isExistingContact));
      const hasAnyContactEligibleForBoth = sortedContactGroups.length > 0 && eligibleContacts.some((c) => !Boolean(c.isExistingContact) && canContactJoinAnyGroup(c));
      const hasAnyContactEligibleForGroup = sortedContactGroups.length > 0 && eligibleContacts.some((c) => canContactJoinAnyGroup(c));

      if (!areAllContactsExisting) {
        setActiveBatchMode("contacts");
      } else if (hasAnyContactEligibleForGroup) {
        setActiveBatchMode("group");
      } else if (hasAnyContactEligibleForBoth) {
        setActiveBatchMode("both");
      } else {
        setActiveBatchMode(null);
      }
    }
  }, [isOpen, eligibleContacts, contactGroups, sortedContactGroups, transactionRecipients, canContactJoinAnyGroup]);

  if (!isOpen || !eligibleContacts || eligibleContacts.length === 0) return null;

  const toggleContact = (email) => {
    if (!email) return;
    setActiveBatchMode(null);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  };

  // Filter contacts by search query
  const filteredContacts = eligibleContacts.filter((c) => {
    const email = getContactEmail(c);
    const fn = getContactFirstName(c);
    const ln = getContactLastName(c);
    const fullName = `${fn} ${ln}`.trim().toLowerCase();
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return email.includes(q) || fullName.includes(q);
  });

  const isAllSelected = filteredContacts.length > 0 && filteredContacts.every((c) => selectedIds.has(getContactEmail(c)));
  const toggleSelectAll = () => {
    setActiveBatchMode(null);
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      const next = new Set(selectedIds);
      filteredContacts.forEach((c) => {
        const email = getContactEmail(c);
        if (email) next.add(email);
      });
      setSelectedIds(next);
    }
  };

  const selectedCount = selectedIds.size;
  const selectedContacts = eligibleContacts.filter((c) => selectedIds.has(getContactEmail(c)));
  const toggleDefaultGroup = (groupId) => {
    if (!groupId) return;
    setDefaultGroupIds((prev) => {
      const exists = prev.includes(groupId);
      const next = exists ? prev.filter((id) => id !== groupId) : [...prev, groupId];
      setContactGroupOverrides((prevOverrides) => {
        const updated = { ...prevOverrides };
        selectedContacts.forEach((c) => {
          const email = getContactEmail(c);
          if (email) {
            updated[email] = [...next];
          }
        });
        return updated;
      });
      return next;
    });
  };

  const setContactMode = (contactEmail, selectedMode, e) => {
    if (e) e.stopPropagation();
    if (!contactEmail) return;
    setActiveBatchMode(null);
    setDestinations((prev) => ({ ...prev, [contactEmail]: selectedMode }));
    if (!selectedIds.has(contactEmail)) {
      setSelectedIds((prev) => new Set([...prev, contactEmail]));
    }
    const currentOverrides = contactGroupOverrides[contactEmail];
    if ((selectedMode === "both" || selectedMode === "group") && (!currentOverrides || currentOverrides.length === 0) && sortedContactGroups.length > 0) {
      const contactObj = eligibleContacts.find((c) => getContactEmail(c) === contactEmail);
      const contactExistingGroupIds = (contactObj?.existingGroupIds || []).map((id) => String(id).toLowerCase().trim());
      const validDefaults = defaultGroupIds.filter((id) => !contactExistingGroupIds.includes(String(id).toLowerCase().trim()));
      let fallbackGroupIds = [];
      if (validDefaults.length > 0) {
        fallbackGroupIds = [...validDefaults];
      } else {
        const availableGroup = sortedContactGroups.find((g) => {
          const gId = String(g.id || g._id || g.Id || "").toLowerCase().trim();
          return gId && !contactExistingGroupIds.includes(gId);
        });
        const firstAvailableGroupId = availableGroup ? (availableGroup.id || availableGroup._id || availableGroup.Id || "") : "";
        fallbackGroupIds = firstAvailableGroupId ? [firstAvailableGroupId] : [];
      }
      setContactGroupOverrides((prev) => ({ ...prev, [contactEmail]: fallbackGroupIds }));
    }
  };

  const toggleContactGroup = (contactEmail, groupId, e) => {
    if (e) e.stopPropagation();
    if (!contactEmail || !groupId) return;

    setContactGroupOverrides((prev) => {
      const current = prev[contactEmail] || [];
      const exists = current.includes(groupId);
      const next = exists ? current.filter((id) => id !== groupId) : [...current, groupId];
      return { ...prev, [contactEmail]: next };
    });

    const currentMode = destinations[contactEmail] || "contacts";
    if (currentMode === "contacts") {
      setDestinations((prev) => ({ ...prev, [contactEmail]: "both" }));
    }

    if (!selectedIds.has(contactEmail)) {
      setSelectedIds((prev) => new Set([...prev, contactEmail]));
    }
  };

  const selectAllGroupsForContact = (contactEmail, e) => {
    if (e) e.stopPropagation();
    if (!contactEmail) return;
    const contactObj = eligibleContacts.find((c) => getContactEmail(c) === contactEmail);
    const contactExistingGroupIds = (contactObj?.existingGroupIds || []).map((id) => String(id).toLowerCase().trim());
    const validGroupIdsToJoin = sortedContactGroups.map((g) => g.id || g._id || g.Id || "").filter((gId) => gId && !contactExistingGroupIds.includes(String(gId).toLowerCase().trim()));
    setContactGroupOverrides((prev) => ({ ...prev, [contactEmail]: validGroupIdsToJoin }));
  };

  const clearGroupsForContact = (contactEmail, e) => {
    if (e) e.stopPropagation();
    if (!contactEmail) return;
    setContactGroupOverrides((prev) => ({ ...prev, [contactEmail]: [] }));
  };

  // -------------------------------------------------------------------------------------------------
  // Parent "Set all to:" toolbar button disabled logic:
  // Each parent button is disabled if and only if ALL child recipient rows have that option disabled.
  // -------------------------------------------------------------------------------------------------
  // 1) "Contacts only" parent button is disabled if ALL recipients already exist in Contacts Directory
  const areAllRecipientsExistingContacts =
    eligibleContacts.length === 0 || eligibleContacts.every((c) => Boolean(c.isExistingContact));
  const isSetAllContactsOnlyDisabled = areAllRecipientsExistingContacts;

  // 2) "Both" parent button is disabled if ALL recipients are ineligible for Both (already existing contact OR already in all groups)
  const areAllRecipientsIneligibleForBoth =
    eligibleContacts.length === 0 ||
    sortedContactGroups.length === 0 ||
    eligibleContacts.every((c) => Boolean(c.isExistingContact) || !canContactJoinAnyGroup(c));
  const isSetAllBothDisabled = areAllRecipientsIneligibleForBoth;

  // 3) "Group only" parent button is disabled if ALL recipients are already members of all available groups
  const areAllRecipientsAlreadyInAllGroups =
    eligibleContacts.length === 0 ||
    sortedContactGroups.length === 0 ||
    eligibleContacts.every((c) => !canContactJoinAnyGroup(c));
  const isSetAllGroupOnlyDisabled = areAllRecipientsAlreadyInAllGroups;

  // Batch setter: updates destination mode across all selected recipients, respecting each recipient's eligibility
  const batchSetMode = (selectedMode) => {
    if (selectedMode === "contacts" && isSetAllContactsOnlyDisabled) return;
    if (selectedMode === "both" && isSetAllBothDisabled) return;
    if (selectedMode === "group" && isSetAllGroupOnlyDisabled) return;

    const firstGroupId = sortedContactGroups.length > 0 ? (sortedContactGroups[0].id || sortedContactGroups[0]._id || sortedContactGroups[0].Id || "") : "";
    const fallbackGroupIds = defaultGroupIds.length > 0 ? defaultGroupIds : (firstGroupId ? [firstGroupId] : []);

    setDestinations((prev) => {
      const updatedDestinations = { ...prev };
      eligibleContacts.forEach((contact) => {
        const contactEmail = getContactEmail(contact);
        if (selectedIds.has(contactEmail)) {
          const isContactExistingInDirectory = Boolean(contact.isExistingContact);
          const canJoinAnyGroup = canContactJoinAnyGroup(contact);

          if (selectedMode === "contacts") {
            if (!isContactExistingInDirectory) {
              updatedDestinations[contactEmail] = "contacts";
            }
          } else if (selectedMode === "both") {
            if (!isContactExistingInDirectory && canJoinAnyGroup) {
              updatedDestinations[contactEmail] = "both";
            } else if (!isContactExistingInDirectory && !canJoinAnyGroup) {
              // Cannot add to group because already in all groups; fallback to contacts
              updatedDestinations[contactEmail] = "contacts";
            } else if (isContactExistingInDirectory && canJoinAnyGroup) {
              // Cannot add to contacts directory because already existing; fallback to group
              updatedDestinations[contactEmail] = "group";
            }
          } else if (selectedMode === "group") {
            if (canJoinAnyGroup) {
              updatedDestinations[contactEmail] = "group";
            } else if (!isContactExistingInDirectory) {
              // Cannot add to group because already in all groups; fallback to contacts
              updatedDestinations[contactEmail] = "contacts";
            }
          }
        }
      });
      return updatedDestinations;
    });

    if (selectedMode === "both" || selectedMode === "group") {
      setContactGroupOverrides((prev) => {
        const updatedOverrides = { ...prev };
        eligibleContacts.forEach((contact) => {
          const contactEmail = getContactEmail(contact);
          if (selectedIds.has(contactEmail) && (!updatedOverrides[contactEmail] || updatedOverrides[contactEmail].length === 0)) {
            const contactExistingGroupIds = (contact.existingGroupIds || []).map((id) => String(id).toLowerCase().trim());
            const validDefaults = defaultGroupIds.filter((id) => !contactExistingGroupIds.includes(String(id).toLowerCase().trim()));
            if (validDefaults.length > 0) {
              updatedOverrides[contactEmail] = [...validDefaults];
            } else {
              const availableGroup = sortedContactGroups.find((g) => {
                const gId = String(g.id || g._id || g.Id || "").toLowerCase().trim();
                return gId && !contactExistingGroupIds.includes(gId);
              });
              const availableGroupId = availableGroup ? (availableGroup.id || availableGroup._id || availableGroup.Id || "") : "";
              if (availableGroupId) updatedOverrides[contactEmail] = [availableGroupId];
            }
          }
        });
        return updatedOverrides;
      });
    }

    setActiveBatchMode(selectedMode);
  };

  const isSaveDisabled = selectedCount === 0 || selectedContacts.some((contact) => {
    const contactEmail = getContactEmail(contact);
    const contactExistingGroupIds = (contact.existingGroupIds || []).map((id) => String(id).toLowerCase().trim());
    const canJoinGroups = canContactJoinAnyGroup(contact);
    const isContactExistingInDirectory = Boolean(contact.isExistingContact);

    // If contact is an existing contact and cannot join any group, they cannot be saved
    if (isContactExistingInDirectory && !canJoinGroups) return true;

    let destinationMode = destinations[contactEmail] || (isContactExistingInDirectory ? (canJoinGroups ? "group" : "contacts") : "contacts");
    if (destinationMode === "both" && (isContactExistingInDirectory || !canJoinGroups)) {
      destinationMode = !canJoinGroups ? "contacts" : "group";
    }
    if (destinationMode === "group" && !canJoinGroups) {
      destinationMode = "contacts";
    }

    if (destinationMode === "both" || destinationMode === "group") {
      const selectedGroupIds = contactGroupOverrides[contactEmail] !== undefined ? contactGroupOverrides[contactEmail] : defaultGroupIds;
      const validGroupIdsToJoin = (selectedGroupIds || []).filter((id) => !contactExistingGroupIds.includes(String(id).toLowerCase().trim()));
      return validGroupIdsToJoin.length === 0;
    }
    return false;
  });

  const handleConfirm = () => {
    const itemDecisions = selectedContacts.map((contact) => {
      const contactEmail = getContactEmail(contact);
      const isContactExistingInDirectory = Boolean(contact.isExistingContact);
      const contactExistingGroupIds = (contact.existingGroupIds || []).map((id) => String(id).toLowerCase().trim());
      const canJoinGroups = canContactJoinAnyGroup(contact);

      let finalDestination = destinations[contactEmail] || (isContactExistingInDirectory ? (canJoinGroups ? "group" : "contacts") : "contacts");
      if (finalDestination === "both" && (isContactExistingInDirectory || !canJoinGroups)) {
        finalDestination = !canJoinGroups ? "contacts" : "group";
      }
      if (finalDestination === "group" && !canJoinGroups) {
        finalDestination = "contacts";
      }

      const assignedGroups = contactGroupOverrides[contactEmail] && contactGroupOverrides[contactEmail].length > 0 ? contactGroupOverrides[contactEmail] : defaultGroupIds;
      const validGroupIds = finalDestination === "both" || finalDestination === "group"
        ? (assignedGroups || []).filter((id) => !contactExistingGroupIds.includes(String(id).toLowerCase().trim()))
        : [];
      return { contact, email: contactEmail, destination: finalDestination, groupId: validGroupIds[0] || null, groupIds: validGroupIds };
    });
    const isAnyGroupDestinationChosen = itemDecisions.some((item) => (item.destination === "both" || item.destination === "group") && item.groupIds && item.groupIds.length > 0);
    onProceed(selectedContacts, shouldSaveGroups && selectedCount > 0, { assignToGroup: isAnyGroupDestinationChosen, targetGroupId: defaultGroupIds[0] || null, targetGroupIds: defaultGroupIds, itemDecisions });
  };

  const handleSkipAll = () => { onProceed([], false, null); };

  return (
    <div className="modalCus">
      <div className="modal-dialog modal-dialog-centered save-directory-modal-dialog">
        <div className="modal-content border-r4" onClick={() => { setOpenDropdownEmail(null); setIsDefaultGroupsOpen(false); }}>
          {/* Header */}
          <div className="modal-header d-flex align-items-center justify-content-between">
            <div>
              <h4 className="mb-0 text-danger fw-bold font-20 d-flex align-items-center gap-2">
                <span className="material-icons font-22 text-danger">person_add</span>
                {selectedLanguage?.LstLanguageCodes?.UI_SaveRecipientsToContactsAndGroups}
              </h4>
            </div>
            <button type="button" className="btn btn-outline-danger Readerclose modalclose download mt-0" onClick={onClose} title={selectedLanguage.LstLanguageCodes.UI_Close}>
              <span className="material-icons font-20">highlight_off</span>
            </button>
          </div>

          {/* Top Control Bar: Default Target Group & Search */}
          {sortedContactGroups && sortedContactGroups.length > 0 && (
            <div className="save-directory-top-bar">
              <div className="save-directory-top-controls">
                <div className="save-directory-group-select-wrapper">
                  <span className="save-directory-label d-flex align-items-center gap-1">
                    <span className="material-icons font-18 text-primary">groups</span>
                    {selectedLanguage?.LstLanguageCodes?.UI_DefaultGroup}
                  </span>

                  {/* Multi-Select Default Group Dropdown */}
                  <div className="save-directory-dropdown-wrapper">
                    <button ref={defaultGroupTriggerRef} type="button" className="save-directory-dropdown-trigger" title={selectedLanguage.LstLanguageCodes.UI_SelectDefaultGroups}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDefaultGroupsSearch("");
                        setOpenDropdownEmail(null);
                        setIsDefaultGroupsOpen((prev) => !prev);
                      }}
                    >
                      <span className="save-directory-dropdown-trigger-text">
                        {defaultGroupIds.length === 0 ? selectedLanguage.LstLanguageCodes.UI_SelectDefaultGroups : defaultGroupIds.length === 1 ? sortedContactGroups.find((g) => (g.id || g._id || g.Id) === defaultGroupIds[0])?.name ||
                          sortedContactGroups.find((g) => (g.id || g._id || g.Id) === defaultGroupIds[0])?.Name || selectedLanguage.LstLanguageCodes.UI_GroupOne : `${defaultGroupIds.length} Groups Selected`}
                      </span>
                      <span className="material-icons save-directory-select-arrow">keyboard_arrow_down</span>
                    </button>

                    <PortalGroupDropdown triggerRef={defaultGroupTriggerRef} isOpen={isDefaultGroupsOpen} onClose={() => setIsDefaultGroupsOpen(false)} title={selectedLanguage.LstLanguageCodes.UI_SelectMultipleGroups}
                      actionText={defaultGroupIds.length === sortedContactGroups.length ? selectedLanguage.LstLanguageCodes.UI_ClearAll : selectedLanguage.LstLanguageCodes.UI_SelectAll}
                      onAction={() => {
                        if (defaultGroupIds.length === sortedContactGroups.length) {
                          setDefaultGroupIds([]);
                          setContactGroupOverrides((prev) => {
                            const next = { ...prev };
                            selectedContacts.forEach((c) => {
                              const email = getContactEmail(c);
                              if (email) next[email] = [];
                            });
                            return next;
                          });
                        } else {
                          const allIds = sortedContactGroups.map((g) => g.id || g._id || g.Id || "").filter(Boolean);
                          setDefaultGroupIds(allIds);
                          setContactGroupOverrides((prev) => {
                            const next = { ...prev };
                            selectedContacts.forEach((c) => {
                              const email = getContactEmail(c);
                              if (email) next[email] = [...allIds];
                            });
                            return next;
                          });
                        }
                      }}
                      searchValue={defaultGroupsSearch}
                      onSearchChange={setDefaultGroupsSearch}
                      searchPlaceholder={selectedLanguage.LstLanguageCodes.UI_SearchGroups}
                      items={sortedContactGroups.filter((g) => {
                        if (!defaultGroupsSearch.trim()) return true;
                        const q = defaultGroupsSearch.toLowerCase().trim();
                        return (g.name || g.Name || "").toLowerCase().includes(q);
                      }).map((g) => {
                        const gId = g.id || g._id || g.Id || "";
                        return {
                          id: gId,
                          name: g.name || g.Name || "Group",
                          isChecked: defaultGroupIds.includes(gId),
                        };
                      })}
                      onToggleItem={(gId) => toggleDefaultGroup(gId)}
                    />
                  </div>
                </div>

                <div className="save-directory-search-container readerEdit-mail">
                  <span className="material-icons save-directory-search-icon">
                    search
                  </span>
                  <input type="text" placeholder={selectedLanguage?.LstLanguageCodes?.UI_SearchRecipients} value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)} className="save-directory-search-input clsFnLn form-control" />
                  {searchQuery && (
                    <button type="button" onClick={() => setSearchQuery("")} className="save-directory-search-clear">
                      <span className="material-icons save-directory-search-clear-icon">close</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Toolbar: Select All & Batch Setters */}
          <div className="save-directory-toolbar">
            <button type="button" onClick={toggleSelectAll} className="save-directory-checkbox-btn">
              <div className={`save-directory-custom-checkbox ${isAllSelected ? "checked" : ""}`}>
                {isAllSelected && <span className="material-icons">check</span>}
              </div>
              <span className="font-13 font-w600 text-dark">
                {selectedLanguage?.LstLanguageCodes?.UI_SelectAll}
              </span>
            </button>

            {/* Quick Batch Set Buttons */}
            <div className="save-directory-batch-setters">
              <span className="font-11 text-muted fw-bold me-1">
                {selectedLanguage?.LstLanguageCodes?.UI_SetAllTo}
              </span>
              <button
                type="button"
                disabled={isSetAllContactsOnlyDisabled}
                onClick={() => !isSetAllContactsOnlyDisabled && batchSetMode("contacts")}
                className={`save-directory-batch-btn ${activeBatchMode === "contacts" && !isSetAllContactsOnlyDisabled ? "active" : ""} ${isSetAllContactsOnlyDisabled ? "disabled opacity-50 cursor-not-allowed" : ""}`}
                style={isSetAllContactsOnlyDisabled ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                title={isSetAllContactsOnlyDisabled ? "All recipients already exist in contacts directory" : selectedLanguage.LstLanguageCodes.UI_ContactsOnly}
              >
                {selectedLanguage.LstLanguageCodes.UI_ContactsOnly}
              </button>
              <button
                type="button"
                disabled={isSetAllBothDisabled}
                onClick={() => !isSetAllBothDisabled && batchSetMode("both")}
                className={`save-directory-batch-btn ${activeBatchMode === "both" && !isSetAllBothDisabled ? "active" : ""} ${isSetAllBothDisabled ? "disabled opacity-50 cursor-not-allowed" : ""}`}
                style={isSetAllBothDisabled ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                title={isSetAllBothDisabled ? "No recipients can be saved to both contacts and groups" : selectedLanguage?.LstLanguageCodes?.UI_Both}
              >
                {selectedLanguage?.LstLanguageCodes?.UI_Both}
              </button>
              <button
                type="button"
                disabled={isSetAllGroupOnlyDisabled}
                onClick={() => !isSetAllGroupOnlyDisabled && batchSetMode("group")}
                className={`save-directory-batch-btn ${activeBatchMode === "group" && !isSetAllGroupOnlyDisabled ? "active" : ""} ${isSetAllGroupOnlyDisabled ? "disabled opacity-50 cursor-not-allowed" : ""}`}
                style={isSetAllGroupOnlyDisabled ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                title={isSetAllGroupOnlyDisabled ? "All recipients are already in all available groups" : selectedLanguage?.LstLanguageCodes?.UI_GroupOnly}
              >
                {selectedLanguage?.LstLanguageCodes?.UI_GroupOnly}
              </button>
            </div>
          </div>

          {/* Contact List */}
          <div className="save-directory-list">
            {filteredContacts.length === 0 ? (
              <div className="save-directory-empty">{selectedLanguage?.LstLanguageCodes?.UI_NoMatchingRecipientsFound}</div>
            ) : (
              filteredContacts.map((contact, index) => {
                const contactEmail = getContactEmail(contact);
                const isContactSelected = selectedIds.has(contactEmail);
                const firstName = getContactFirstName(contact);
                const lastName = getContactLastName(contact);
                const displayName = [firstName, lastName].filter(Boolean).join(" ");
                const isExistingContactInDirectory = Boolean(contact.isExistingContact);
                const contactExistingGroupIds = contact.existingGroupIds || [];
                const canJoinAnyGroup = canContactJoinAnyGroup(contact);
                const isAlreadyMemberOfAllGroups = !canJoinAnyGroup;

                // Per-contact option disabled rules:
                // 1) Contacts Only: disabled if recipient already exists in Contacts Directory
                const isContactsOnlyDisabled = isExistingContactInDirectory;
                // 2) Both: disabled if recipient already exists in Contacts Directory OR is already in all groups
                const isBothDisabled = isExistingContactInDirectory || isAlreadyMemberOfAllGroups;
                // 3) Group Only: disabled if recipient is already a member of all available groups
                const isGroupOnlyDisabled = isAlreadyMemberOfAllGroups;

                // Resolve effective destination mode with fallbacks when an option is disabled
                let effectiveDestinationMode = destinations[contactEmail] || (isExistingContactInDirectory ? (canJoinAnyGroup ? "group" : "contacts") : "contacts");
                if (effectiveDestinationMode === "both" && isBothDisabled) {
                  effectiveDestinationMode = !isContactsOnlyDisabled ? "contacts" : (canJoinAnyGroup ? "group" : "contacts");
                } else if (effectiveDestinationMode === "group" && isGroupOnlyDisabled) {
                  effectiveDestinationMode = "contacts";
                } else if (effectiveDestinationMode === "contacts" && isContactsOnlyDisabled) {
                  effectiveDestinationMode = canJoinAnyGroup ? "group" : "contacts";
                }

                const assignedGroupIds = contactGroupOverrides[contactEmail] || defaultGroupIds;
                const isDropdownOpen = openDropdownEmail === contactEmail;

                return (
                  <div key={contactEmail} onClick={() => toggleContact(contactEmail)} className={`save-directory-card ${isContactSelected ? "selected" : "unselected"}`}>
                    {/* Left: Checkbox + Avatar + Name/Email */}
                    <div className="save-directory-card-left">
                      <div className={`save-directory-custom-checkbox ${isContactSelected ? "checked" : ""}`}>
                        {isContactSelected && <span className="material-icons">check</span>}
                      </div>
                      <div className="save-directory-info">
                        <p className="save-directory-name">
                          {displayName || contactEmail}
                        </p>
                        {displayName && <p className="save-directory-email">{contactEmail}</p>}
                      </div>
                    </div>

                    {/* Right: Destination Pills & Per-Contact Multi-Group Selector */}
                    {isContactSelected ? (
                      <div className="save-directory-card-right" onClick={(e) => e.stopPropagation()}>
                        {/* Per-contact multi-group selector dropdown: only show when destination includes group and recipient can join groups */}
                        {(effectiveDestinationMode === "both" || effectiveDestinationMode === "group") && canJoinAnyGroup && sortedContactGroups.length > 0 && (
                          <div className="save-directory-dropdown-wrapper">
                            <button ref={(el) => { if (el) rowTriggerRefs.current[contactEmail] = el; }} type="button" className="save-directory-dropdown-trigger"
                              title={selectedLanguage.LstLanguageCodes.UI_SelectOneOrMultipleGroupsForThisContact}
                              onClick={(e) => {
                                e.stopPropagation();
                                setGroupDropdownSearch("");
                                setIsDefaultGroupsOpen(false);
                                setOpenDropdownEmail(isDropdownOpen ? null : contactEmail);
                              }}
                            >
                              <span className="save-directory-dropdown-trigger-text">
                                {assignedGroupIds.length === 0 ? selectedLanguage.LstLanguageCodes.UI_SelectGroups : assignedGroupIds.length === 1 ? sortedContactGroups.find((g) => (g.id || g._id || g.Id) === assignedGroupIds[0])?.name ||
                                  sortedContactGroups.find((g) => (g.id || g._id || g.Id) === assignedGroupIds[0])?.Name || selectedLanguage.LstLanguageCodes.UI_GroupOne : `${assignedGroupIds.length} Groups Selected`}
                              </span>
                              <span className="material-icons save-directory-select-arrow">keyboard_arrow_down</span>
                            </button>
                            <PortalGroupDropdown triggerRef={{ current: rowTriggerRefs.current[contactEmail] }} isOpen={isDropdownOpen} onClose={() => setOpenDropdownEmail(null)} title={selectedLanguage.LstLanguageCodes.UI_AssignMultipleGroups}
                              actionText={assignedGroupIds.length === sortedContactGroups.length ? selectedLanguage.LstLanguageCodes.UI_ClearAll : selectedLanguage.LstLanguageCodes.UI_SelectAll}
                              onAction={() => {
                                if (assignedGroupIds.length === sortedContactGroups.length) {
                                  clearGroupsForContact(contactEmail);
                                } else {
                                  selectAllGroupsForContact(contactEmail);
                                }
                              }}
                              searchValue={groupDropdownSearch} onSearchChange={setGroupDropdownSearch} searchPlaceholder={selectedLanguage.LstLanguageCodes.UI_SearchGroups}
                              items={sortedContactGroups.filter((g) => {
                                if (!groupDropdownSearch.trim()) return true;
                                const q = groupDropdownSearch.toLowerCase().trim();
                                return (g.name || g.Name || "").toLowerCase().includes(q);
                              }).map((g) => {
                                const gId = g.id || g._id || g.Id || "";
                                const isAlreadyMember = (contactExistingGroupIds || []).some((eId) => String(eId).toLowerCase().trim() === String(gId).toLowerCase().trim());
                                return {
                                  id: gId,
                                  name: g.name || g.Name || "Group",
                                  isChecked: assignedGroupIds.includes(gId) || isAlreadyMember,
                                  isDisabled: isAlreadyMember,
                                  badgeText: isAlreadyMember ? "(Already in group)" : null
                                };
                              })}
                              onToggleItem={(gId) => {
                                const isAlreadyMember = (contactExistingGroupIds || []).some((eId) => String(eId).toLowerCase().trim() === String(gId).toLowerCase().trim());
                                if (!isAlreadyMember) {
                                  toggleContactGroup(contactEmail, gId);
                                }
                              }}
                            />
                          </div>
                        )}

                        {/* Destination Pills */}
                        <div className="save-directory-pills">
                          <button
                            type="button"
                            disabled={isContactsOnlyDisabled}
                            onClick={(e) => !isContactsOnlyDisabled && setContactMode(contactEmail, "contacts", e)}
                            className={`save-directory-pill-btn ${effectiveDestinationMode === "contacts" ? "active mode-contacts" : ""} ${isContactsOnlyDisabled ? "disabled opacity-50 cursor-not-allowed" : ""}`}
                            style={isContactsOnlyDisabled ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                            title={isContactsOnlyDisabled ? "Contact already exists in contacts directory" : selectedLanguage.LstLanguageCodes.UI_SaveToContactsDirectoryOnly}
                          >
                            <span className="material-icons">person</span>
                            {selectedLanguage.LstLanguageCodes.UI_ContactsOnly}
                          </button>

                          {sortedContactGroups && sortedContactGroups.length > 0 && (
                            <>
                              <button
                                type="button"
                                disabled={isBothDisabled}
                                onClick={(e) => !isBothDisabled && setContactMode(contactEmail, "both", e)}
                                className={`save-directory-pill-btn ${effectiveDestinationMode === "both" ? "active mode-both" : ""} ${isBothDisabled ? "disabled opacity-50 cursor-not-allowed" : ""}`}
                                style={isBothDisabled ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                                title={
                                  isExistingContactInDirectory
                                    ? "Contact already exists in contacts directory"
                                    : isAlreadyMemberOfAllGroups
                                      ? "Contact already exists in all groups"
                                      : selectedLanguage.LstLanguageCodes.UI_SaveToContactsDirectoryAndSelectedGroup
                                }
                              >
                                <span className="material-icons">layers</span>
                                {selectedLanguage?.LstLanguageCodes?.UI_Both}
                              </button>
                              <button
                                type="button"
                                disabled={isGroupOnlyDisabled}
                                onClick={(e) => !isGroupOnlyDisabled && setContactMode(contactEmail, "group", e)}
                                className={`save-directory-pill-btn ${effectiveDestinationMode === "group" ? "active mode-group" : ""} ${isGroupOnlyDisabled ? "disabled opacity-50 cursor-not-allowed" : ""}`}
                                style={isGroupOnlyDisabled ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                                title={
                                  isAlreadyMemberOfAllGroups
                                    ? "Contact already exists in all groups"
                                    : selectedLanguage?.LstLanguageCodes?.UI_GroupOnly || "Group Only"
                                }
                              >
                                <span className="material-icons">groups</span>
                                {selectedLanguage?.LstLanguageCodes?.UI_GroupOnly}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="save-directory-skipped-text"></span>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer Bar */}
          <div className="save-directory-footer">
            <div className="save-directory-footer-summary"></div>
            <div className="save-directory-footer-actions">
              <button type="button" onClick={handleSkipAll} className="btn btn-outline-danger px-4 border-r4 font-13 fw-bold">
                {selectedLanguage?.LstLanguageCodes?.UI_Skip}
              </button>
              <button type="button" onClick={handleConfirm} disabled={isSaveDisabled} className="btn btn-primary px-4 border-r4 font-13 fw-bold">
                {selectedLanguage?.LstLanguageCodes?.UI_Save}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SaveToDirectoryModal;